/*
 * Copyright (c) 2020 SQUAREPING INC.
 *
 * SQUAREPING CONFIDENTIAL
 * [2019 and Beyond] SQUAREPING INC. All Rights Reserved.
 *
 * NOTICE: The source code contained herein is, and remains the property of SQUAREPING INC.
 * and its suppliers, if any. The intellectual and technical concepts contained herein are
 * proprietary to SQUAREPING INC. and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 *
 * Dissemination of this information or reproduction of this material is strictly forbidden
 * anywhere in the world unless prior written permission is obtained from SQUAREPING INC.
 */

import { StringUtils } from '../utils/string';
import { ExtensionType, PingGroup, PingGroupAssetType } from '../types/pingGroup';
import { User } from '../types/user';
import axios from 'axios';
import { COOKIE_NAMES, GROUP_API_ENDPOINT } from '../constants';
import Cookies from 'js-cookie';
import { AxiosUtil } from '../utils/axios';

const httpGroup = axios.create({
  baseURL: GROUP_API_ENDPOINT,
});

const accessToken = Cookies.get(COOKIE_NAMES.COOKIE_ACCESS_TOKEN);
if (!httpGroup.defaults.headers['Authorization'] && accessToken) {
  httpGroup.defaults.headers['Authorization'] = `Bearer ${accessToken}`;
}
AxiosUtil.handleAutoRefreshToken(httpGroup);
httpGroup.interceptors.response.use((response: { data: any }) => response.data);

type APIRes<T = any> = Promise<{
  data: T;
  code: number;
}>;

export const PingGroupService = {
  getBusinessPingGroups(params: {
    groupId: string;
    groupType: string;
    page: number;
    pageSize: number;
    search: string;
    sort: string;
    pingType?: PingGroupAssetType | string;
  }): APIRes<{ pingGroups: PingGroup[]; total: number }> {
    const url = StringUtils.addParamsToUrl('/ping-groups/', params);
    return httpGroup.get(url);
  },

  getPingGroup(pingGroupId: string): APIRes<PingGroup> {
    return httpGroup.get(`/ping-groups/${pingGroupId}`);
  },

  createPingGroup({ groupType, groupId, data }: { groupType: ExtensionType; groupId: string; data: Partial<PingGroup> }): APIRes<PingGroup> {
    return httpGroup.post(`/ping-groups`, { ...data, groupType, groupId });
  },

  createPingGroups(pingGroups: Array<PingGroup>): APIRes<PingGroup[]> {
    return httpGroup.post(`/ping-groups/bulk`, { pingGroups });
  },

  updateMember({
    pingGroupId,
    members,
  }: {
    pingGroupId: string;
    members: Array<{
      user: string;
      _id?: string;
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: string;
      phoneNumberCountryCode: string;
      phoneNumberCallingCode: string;
      passcode?: string;
    }>;
  }): APIRes<PingGroup> {
    return httpGroup.patch(`/ping-groups/${pingGroupId}`, { members });
  },

  updateMemberById({
    pingGroupId,
    userId,
    data,
  }: {
    pingGroupId: string;
    userId: string;
    data: {
      firstName?: string;
      lastName?: string;
      phoneNumber?: string;
      phoneNumberUserInput?: string;
      phoneNumberCountryCode?: string;
      phoneNumberCallingCode?: string;
    };
  }): APIRes<PingGroup> {
    return httpGroup.patch(`/ping-groups/${pingGroupId}/members/${userId}`, data);
  },

  updateRoleMember({ pingGroupId, owner }: { pingGroupId: string; owner: string | null }): APIRes<PingGroup> {
    return httpGroup.patch(`/ping-groups/${pingGroupId}`, { owner });
  },

  deleteMember(pingGroupId: string, userId?: string): APIRes<void> {
    return httpGroup.delete(`/ping-groups/${pingGroupId}/members/${userId}`);
  },

  updatePingGroup({ pingGroupId, data }: { pingGroupId: string; data: Partial<PingGroup> }): APIRes<PingGroup> {
    return httpGroup.patch(`/ping-groups/${pingGroupId}`, data);
  },

  deletePingGroup(pingGroupId: string): APIRes<void> {
    return httpGroup.delete(`/ping-groups/${pingGroupId}`);
  },

  deletePingGroups(ids: string[]): APIRes<void> {
    return httpGroup.post(`/ping-groups/delete-many`, {
      ids: ids.join(','),
    });
  },

  resetAllByBusiness(businessId: string): APIRes<void> {
    return httpGroup.post(`/ping-groups/reset-all`, { businessId });
  },

  /**
   * Extension API
   */
  createExtensions({
    pingGroupConfigs,
    businessId,
    types,
    pingType,
  }: {
    pingGroupConfigs: Array<PingGroup>;
    businessId: string;
    types: Array<ExtensionType>;
    pingType: string;
  }): APIRes<PingGroup[]> {
    return httpGroup.post(`/ping-groups/extensions/bulk`, { pingGroupConfigs, businessId, types, pingType });
  },

  resetBusinessExtension({ businessId, toExtensionType, extensionIds }: { businessId: string; toExtensionType: ExtensionType; extensionIds?: string[] }): APIRes<void> {
    return httpGroup.patch(`/ping-groups/extensions/reset`, {
      businessId,
      toExtensionType,
      extensionIds: (extensionIds || []).join(','),
    });
  },

  updateBusinessExtensionSetting({ businessId }: { businessId: string }): APIRes<void> {
    return httpGroup.patch(`/ping-groups/extensions/update-setting`, {
      businessId,
    });
  },

  countExtensions({ businessId }: { businessId: string }): APIRes<number> {
    return httpGroup.post(`/ping-groups/extensions/count`, { businessId });
  },

  /**
   * Business stats (extensions + service tags) for many businesses at once
   */
  getBusinessStats(data: { businessIds: string[] }): APIRes<
    Record<
      string,
      {
        extensionCount: number;
        activeExtensionCount: number;
        serviceTagCount: number;
        activeServiceTagCount: number;
        postCount: number;
        activePostCount: number;
        pingGroupAboveMemberLimitCount: number;
      }
    >
  > {
    return httpGroup.post(`/ping-groups/extensions/business-stats`, data);
  },

  claimAnoExtension(body: {
    businessId: string;
    extensionCode: string;
    claimCode?: string;
    phoneNumber: string;
    phoneNumberUserInput: string;
    phoneNumberCountryCode: string;
    name: string;
  }): APIRes<PingGroup> {
    return httpGroup.post(`/ping-groups/extensions/claim-anon`, body);
  },

  /**
   * On-board a user to an extension (admin/CMS flow): create user profile and assign to extension.
   * Backend may create user account with temporary password and link to extension.
   */
  onboardExtension(body: {
    businessId: string;
    extensionCode: string;
    claimCode?: string;
    firstName?: string;
    lastName?: string;
    email: string;
    phoneNumber?: string;
    phoneNumberCountryCode?: string;
    phoneNumberUserInput?: string;
    temporaryPassword?: string;
  }): APIRes<PingGroup> {
    return httpGroup.post(`/ping-groups/extensions/onboard`, body);
  },

  bulkOnboardExtensions(body: {
    extensions: Array<{
      businessId: string;
      extensionCode: string;
      email: string;
      claimCode?: string;
      firstName?: string;
      lastName?: string;
      phoneNumber?: string;
      temporaryPassword?: string;
    }>;
  }): APIRes<{
    nofFailedTransactions: number;
    failedTransactions: Array<{ transaction: Record<string, unknown>; errorCode: number; message: string }>;
  }> {
    return httpGroup.post(`/ping-groups/extensions/onboard/bulk`, body);
  },

  /**
   * Resend the onboard welcome email to the extension owner.
   * Only valid while the extension is Inactive (user has not yet logged in).
   */
  resendWelcomeExtension(body: { businessId: string; extensionCode: string }): APIRes<boolean> {
    return httpGroup.post(`/ping-groups/extensions/resend-welcome`, body);
  },

  /**
   * Service Tag API
   */
  unClaimServiceTag(data: { pingGroupId: string }): APIRes<PingGroup> {
    return httpGroup.patch(`/ping-groups/service-tags/${data.pingGroupId}/un-claim`);
  },

  ensureServiceTagsGenerated(data: { businessId: string }): APIRes<PingGroup[]> {
    return httpGroup.post(`/ping-groups/service-tags/${data.businessId}/generate`);
  },

  createServiceTag(data: {
    businessId: string;
    serviceTitle: string;
    serviceIcon: string;
    serviceDescription?: string;
    pingpadServiceType?: string;
    category?: string;
    pingMetadata?: Record<string, any>;
    settings?: Record<string, any>;
  }): APIRes<PingGroup[]> {
    return httpGroup.post(`/ping-groups/service-tags/${data.businessId}/create`, {
      title: data.serviceTitle,
      icon: data.serviceIcon,
      description: data.serviceDescription,
      pingpadServiceType: data.pingpadServiceType,
      category: data.category,
      pingMetadata: data.pingMetadata,
      settings: data.settings,
    });
  },

  getServiceTags<P extends boolean>(
    businessId: string,
    populateOwner: P,
    populateMembers: P,
  ): APIRes<P extends true ? (Omit<PingGroup, 'owner'> & { owner: User })[] : PingGroup[]> {
    return httpGroup.get(StringUtils.addParamsToUrl(`/ping-groups/service-tags/${businessId}`, { populateOwner, populateMembers }));
  },

  resetServiceTag(pingGroupId: string): APIRes<PingGroup> {
    return httpGroup.patch(`/ping-groups/service-tags/${pingGroupId}/reset`);
  },

  checkoutMember({ pingGroupId, userId }: { pingGroupId: string; userId: string }): APIRes<any> {
    return httpGroup.post(`/ping-groups/${pingGroupId}/check-out`, { userId });
  },

  updateServiceTagSettings({ pingGroupId, settings }: { pingGroupId: string; settings: any }): APIRes<PingGroup> {
    return httpGroup.patch(`/ping-groups/service-tags/${pingGroupId}/settings`, { settings });
  },
};

export default httpGroup;
